/**
 * B126 — tenant vocabulary packs (Pathik's 2026-08-02 ruling: FIXED packs
 * per tenant type, not per-tenant custom terms; a tenant picks its type at
 * setup and the whole app relabels).
 *
 * The core is ecommerce and the product can be anything — a ferry crossing,
 * a comedy show, a zoo day pass, a t-shirt. The VOCABULARY follows the
 * tenant, not the first vertical we happened to build. This module owns the
 * words; the apps own nothing but lookups.
 *
 * Design walls:
 *   - A FIXED key set: VocabularyPack is a closed interface, and the packs
 *     table is a total Record<TenantType, VocabularyPack>. Adding a key
 *     without filling every pack fails tsc AND the completeness test, so no
 *     pack can silently fall back to ferry words.
 *   - ONE resolution function (vocabularyFor). Nobody indexes the table
 *     directly outside tests; the unset case has exactly one meaning.
 *   - SENTENCE-aware: a noun carries its article and plural, and the
 *     capacity unit carries its preposition ("sold ON this departure" /
 *     "sold AT this showtime" / "sold IN this session"), so stock
 *     sentences read correctly in every pack — pinned by test.
 *   - An UNSET tenant type resolves to GENERIC — honest neutral commerce
 *     words, never a ferry guess.
 *   - GATE-LIST note (B195(e), corrected by B403 #22): B195(e) recorded
 *     "FERRY says manifest — what the law calls it". The 2026-08-26
 *     design measure overruled the DISPLAY word: the frames are drawn in
 *     dev-ferry's own vocabulary and 65:2's card says GATE LIST, so the
 *     FERRY pack now says "gate list" too (the redemption H1 "Redemption
 *     & manifests" is the frame's own hardcoded string, not the pack's).
 *     The wire ops (manifest.download / manifest.print) and tmf1 keep
 *     their names — R5 hides a wire shape, it does not rename it. RETAIL
 *     says "collection list", consistent with the scheduled-pickup
 *     framing that gives it a "time slot" capacity unit.
 *   - RETAIL note: the ruling's worked table marks the retail capacity
 *     unit "—"; the pack renders it honestly as "time slot" (retail with
 *     scheduled pickup/collection reads fine) so every pack stays TOTAL
 *     and no caller needs an is-applicable branch. One place to re-word
 *     if Pathik prefers otherwise.
 *
 * Configuration only — no state machine, no ledger (the one rule).
 */

/* ----------------------------------------------------------------- types */

export const TENANT_TYPES = ["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"] as const;
export type TenantType = (typeof TENANT_TYPES)[number];

/** A noun with everything a sentence needs — the caller never pluralises. */
export interface Noun {
  readonly singular: string;
  readonly plural: string;
  readonly article: "a" | "an";
}

/** The capacity unit additionally knows how a sale sits IN/ON/AT it. */
export interface SlotNoun extends Noun {
  readonly preposition: string;
}

/**
 * The FIXED key set. Every pack fills every key; extending this interface
 * without extending every pack below is a compile error first and a test
 * failure second.
 */
export interface VocabularyPack {
  /** The sellable moment: departure / showtime / session / tour / time slot. */
  readonly capacityUnit: SlotNoun;
  /** What the capacity unit does at its time: departs / starts / opens / begins. */
  readonly capacityUnitVerb: string;
  /** Where it happens: terminal / venue / park / site / store. */
  readonly place: Noun;
  /** The grouping operators plan by: route / season / park / attraction / shop. */
  readonly placeGroup: Noun;
  /** The buyer's experience: journey / show / visit / tour / purchase. */
  readonly journeyOrVisit: Noun;
  /** What grants entry: ticket / ticket / admission / ticket / order. */
  readonly admission: Noun;
  /** Who runs the operation, in their own words. */
  readonly operatorNoun: Noun;
  /**
   * B195(e) — the printed/signed list of who may come in, used at the
   * gate and downloadable to a scanner: manifest / door list / gate list
   * / collection list. FERRY keeps "manifest" because that is what a
   * ferry operator says and what the law calls it; the word reads oddly
   * everywhere else, which is exactly why it belongs to the pack rather
   * than to a screen. NOT the wire noun: `manifest.download` /
   * `manifest.print` are operation names and tmf1 is a document format —
   * those stay, because R5 asks that a wire shape not reach an operator's
   * eye, not that it be renamed.
   */
  readonly gateList: Noun;
  /**
   * B410 (finding #9) — the vertical's own business, plural, as a
   * benchmark subject: "Ferries usually sit under 3%". NOT operatorNoun
   * (that names a person); this names the businesses like this tenant.
   * GENERIC keeps the honest "businesses".
   */
  readonly verticalNoun: Noun;
  /**
   * B403 #22 (2026-08-26 design measure) — where the scanners stand, in
   * the tenant's own word: pier / door / gate / gate / counter. 65:2
   * (drawn in dev-ferry's vocabulary) says "Scanners at the pier";
   * GENERIC keeps the honest "gate".
   */
  readonly pierNoun: Noun;
  /**
   * B403 #22 — what an admitted guest does at the checkpoint, a bare
   * verb phrase for "who may {boardVerb}": board / come in / enter /
   * enter / collect. 65:2's gate-list card says "who may board".
   */
  readonly boardVerb: string;
  /**
   * B403 #22 — the launch wizard's question verb, "Who can {rideVerb}?".
   * 43:506 (dev-ferry's words) says "Who can ride?"; an unset tenant
   * honestly asks "Who can come?".
   */
  readonly rideVerb: string;
  /**
   * B403 #22-extension (Pathik measure, 2026-08-26) — the BUYER-sentence
   * word for the sellable moment: "on the same sailing", "Next sailing
   * 18:00". Distinct from capacityUnit, the OPERATOR register the admin
   * speaks (B195(e)'s ruled "departure") — 75:2/72:2 draw dev-ferry's own
   * buyer voice, and the 08-26 ruling keeps both: label registers (the
   * tickets DEPARTURE section, cardMeta's "timed departure") stay on the
   * operator word because the frames draw them that way. Non-ferry packs
   * speak their capacityUnit; GENERIC stays the honest neutral word.
   */
  readonly departureNoun: Noun;
  /**
   * B403 #22-extension — plain buyer forms for catalog nouns the frames
   * shorten: FERRY says "bikes" where the catalog variant is "Bicycle".
   * Keyed by the lowercased, qualifier-stripped singular; the value is the
   * plain SINGULAR ("bicycle" -> "bike") — pluralisation stays the
   * caller's derivation, so irregulars keep working. Empty where a
   * vertical has no such forms; a miss falls through to the catalog word.
   */
  readonly plainForms: Readonly<Record<string, string>>;
  /**
   * B614 (Pathik 2026-10-01, on the Phoenix demo: "the CTA buttons are very
   * weird") — the catalog card's ONE action, where the vertical's products
   * are PEOPLE and the verb-object derivation ("Book a Barbosa") reads as
   * nonsense. Absent = the standing derivation (Book a Crossing / Buy a Day
   * Pass). SHOW says "Get tickets"; the card's meta line drops its "timed
   * departure" clause with it — a poster's nights say when.
   */
  readonly cardCta?: string;
  /**
   * B630 — the till's ask-bar example, in this vertical's own sale: the
   * placeholder hard-coded the FERRY sentence (B403 #31 (d)), so a comedy
   * club's box office was invited to sell "two adults and a bike".
   */
  readonly askExample: string;
}

/* ----------------------------------------------------------------- packs */

function noun(singular: string, plural: string, article: "a" | "an" = "a"): Noun {
  return Object.freeze({ singular, plural, article });
}

const NO_PLAIN_FORMS: Readonly<Record<string, string>> = Object.freeze({});

function slotNoun(singular: string, plural: string, preposition: string, article: "a" | "an" = "a"): SlotNoun {
  return Object.freeze({ singular, plural, article, preposition });
}

export const VOCABULARY_PACKS: Record<TenantType, VocabularyPack> = Object.freeze({
  FERRY: Object.freeze({
    capacityUnit: slotNoun("departure", "departures", "on"),
    capacityUnitVerb: "departs",
    place: noun("terminal", "terminals"),
    placeGroup: noun("route", "routes"),
    journeyOrVisit: noun("journey", "journeys"),
    admission: noun("ticket", "tickets"),
    operatorNoun: noun("ferry manager", "ferry managers"),
    /* B403 #22 — the frame's word (65:2), overruling B195(e)'s "manifest". */
    gateList: noun("gate list", "gate lists"),
    verticalNoun: noun("ferry", "ferries"),
    pierNoun: noun("pier", "piers"),
    boardVerb: "board",
    rideVerb: "ride",
    /* B403 #22-extension — dev-ferry's buyer voice (75:2/72:2): sailings and bikes. */
    departureNoun: noun("sailing", "sailings"),
    plainForms: Object.freeze({ bicycle: "bike" }),
    askExample: "two adults and a bike on the 10:30",
  }),
  SHOW: Object.freeze({
    capacityUnit: slotNoun("showtime", "showtimes", "at"),
    capacityUnitVerb: "starts",
    place: noun("venue", "venues"),
    placeGroup: noun("season", "seasons"),
    journeyOrVisit: noun("show", "shows"),
    admission: noun("ticket", "tickets"),
    operatorNoun: noun("box office manager", "box office managers"),
    gateList: noun("door list", "door lists"),
    verticalNoun: noun("show", "shows"),
    pierNoun: noun("door", "doors"),
    boardVerb: "come in",
    rideVerb: "come",
    departureNoun: noun("showtime", "showtimes"),
    plainForms: NO_PLAIN_FORMS,
    cardCta: "Get tickets",
    askExample: "two GA for the 9:45",
  }),
  PARK: Object.freeze({
    capacityUnit: slotNoun("session", "sessions", "in"),
    capacityUnitVerb: "opens",
    place: noun("park", "parks"),
    placeGroup: noun("park", "parks"),
    journeyOrVisit: noun("visit", "visits"),
    admission: noun("admission", "admissions", "an"),
    operatorNoun: noun("park manager", "park managers"),
    gateList: noun("gate list", "gate lists"),
    verticalNoun: noun("park", "parks"),
    pierNoun: noun("gate", "gates"),
    boardVerb: "enter",
    rideVerb: "visit",
    departureNoun: noun("session", "sessions"),
    plainForms: NO_PLAIN_FORMS,
    askExample: "two adults for the 10:00 session",
  }),
  ATTRACTION: Object.freeze({
    capacityUnit: slotNoun("tour", "tours", "on"),
    capacityUnitVerb: "begins",
    place: noun("site", "sites"),
    placeGroup: noun("attraction", "attractions", "an"),
    journeyOrVisit: noun("tour", "tours"),
    admission: noun("ticket", "tickets"),
    operatorNoun: noun("tour operator", "tour operators"),
    gateList: noun("gate list", "gate lists"),
    verticalNoun: noun("attraction", "attractions", "an"),
    pierNoun: noun("gate", "gates"),
    boardVerb: "enter",
    rideVerb: "visit",
    departureNoun: noun("tour", "tours"),
    plainForms: NO_PLAIN_FORMS,
    askExample: "two adults on the 2:00 tour",
  }),
  RETAIL: Object.freeze({
    capacityUnit: slotNoun("time slot", "time slots", "in"),
    capacityUnitVerb: "opens",
    place: noun("store", "stores"),
    placeGroup: noun("shop", "shops"),
    journeyOrVisit: noun("purchase", "purchases"),
    admission: noun("order", "orders", "an"),
    operatorNoun: noun("store manager", "store managers"),
    gateList: noun("collection list", "collection lists"),
    verticalNoun: noun("shop", "shops"),
    pierNoun: noun("counter", "counters"),
    boardVerb: "collect",
    rideVerb: "order",
    departureNoun: noun("time slot", "time slots"),
    plainForms: NO_PLAIN_FORMS,
    askExample: "two of the first item for the 10:30 slot",
  }),
});

/**
 * The honest default for a tenant that has not picked a type yet: neutral
 * commerce words. Deliberately NOT ferry — defaulting to the first vertical
 * would be guessing, and dev-event must never say "departure".
 */
export const GENERIC_VOCABULARY: VocabularyPack = Object.freeze({
  capacityUnit: slotNoun("time slot", "time slots", "in"),
  capacityUnitVerb: "starts",
  place: noun("location", "locations"),
  placeGroup: noun("collection", "collections"),
  journeyOrVisit: noun("visit", "visits"),
  admission: noun("ticket", "tickets"),
  operatorNoun: noun("manager", "managers"),
  gateList: noun("gate list", "gate lists"),
  verticalNoun: noun("business", "businesses"),
  pierNoun: noun("gate", "gates"),
  boardVerb: "come in",
  rideVerb: "come",
  departureNoun: noun("time slot", "time slots"),
  plainForms: NO_PLAIN_FORMS,
  askExample: "two tickets for the 10:30",
});

/* -------------------------------------------------------------- resolver */

/**
 * THE resolution function — the only door from a tenant's stored type to
 * words on a screen. Unset resolves to GENERIC, never to a guessed type.
 */
export function vocabularyFor(type: TenantType | undefined): VocabularyPack {
  return type === undefined ? GENERIC_VOCABULARY : VOCABULARY_PACKS[type];
}

/** True membership check for the write door — refusals name the stranger. */
export function isTenantType(value: string): value is TenantType {
  return (TENANT_TYPES as readonly string[]).includes(value);
}

/* --------------------------------------------------------------- helpers */

/** "1 departure" / "3 departures" — pluralisation is the pack's job. */
export function countNoun(n: Noun, count: number): string {
  return `${String(count)} ${count === 1 ? n.singular : n.plural}`;
}

/* ------------------------------------------------------------- changes */

/** One word-swap row: the neutral word and the pack's, articles carried. */
export interface VocabularyChange {
  readonly from: string;
  readonly to: string;
}

/**
 * B355 — the "What this changes" card's rows: the GENERIC noun against
 * the chosen pack's, article + singular, ONLY where the word actually
 * changes. A non-change teaches nothing and never surfaces; the verb and
 * preposition stay out because a swap row is a noun's story. Pack order
 * is the interface's key order, so every type lists its swaps the same way.
 */
export function vocabularyChanges(type: TenantType): readonly VocabularyChange[] {
  const pack = VOCABULARY_PACKS[type];
  const keys = ["capacityUnit", "place", "placeGroup", "journeyOrVisit", "admission", "gateList"] as const;
  const rows: VocabularyChange[] = [];
  for (const key of keys) {
    const neutral = GENERIC_VOCABULARY[key];
    const chosen = pack[key];
    if (neutral.singular !== chosen.singular) {
      rows.push({ from: `${neutral.article} ${neutral.singular}`, to: `${chosen.article} ${chosen.singular}` });
    }
  }
  return rows;
}
